import type { ReactNode } from 'react';
import { cx } from '../cx';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';

export type BubbleMapProps = {
  /** The map's coordinate box ("-60 300 460 420" crops to southern and central Finland). */
  viewBox: string;
  /** Region outlines in the viewBox (FINLAND_REGIONS from utils/map/finland-region-geometry). */
  regions: Array<{ id: string; d: string }>;
  /** Towns in the same coordinates (getFinlandMunicipalityMapPoint), with counts. */
  /** `labelSide` moves a label away from a crowded area (defaults by position). */
  points: Array<{
    id: string;
    label: ReactNode;
    x: number;
    y: number;
    value: number;
    display: ReactNode;
    labelSide?: 'left' | 'right';
  }>;
  /** The town the headline is about: sun; the rest context. */
  highlightId?: string;
  /** Towns named on the map; the rest are in the table twin. */
  labelIds?: string[];
  /** The largest circle's radius in map units. */
  maxRadius?: number;
  /** `auto` (default) switches to the narrow form in a narrow container: only the highlighted town is named on the map, the other named towns are a key under it. */
  layout?: ChartLayout;
  /** One sentence that states the finding and the key values, for screen readers. */
  description: string;
  className?: string;
};

/**
 * Circles on the map of Finland sized by count (area, not radius, follows the value): where, by
 * town. Region outlines are quiet; overlapping towns stay in the table twin.
 */
export function BubbleMap({
  viewBox,
  regions,
  points,
  highlightId,
  labelIds,
  maxRadius = 26,
  layout = 'auto',
  description,
  className,
}: BubbleMapProps) {
  const [vx, vy, vw, vh] = viewBox.split(/\s+/).map(Number);
  const top = Math.max(1, ...points.map((p) => p.value));
  const r = (v: number) => 2 + maxRadius * Math.sqrt(v / top);
  const named = new Set(labelIds ?? points.map((p) => p.id));
  const sorted = [...points].sort((a, b) => b.value - a.value);
  const keyed = points.filter((p) => named.has(p.id) && p.id !== highlightId);
  return (
    <div role="img" aria-label={description} className={cx('@container w-full max-w-96', className)}>
      <div className="relative">
        <svg viewBox={viewBox} className="block h-auto w-full" aria-hidden="true">
          {regions.map((g) => (
            <path key={g.id} d={g.d} className="fill-jc-elevated stroke-jc-surface" strokeWidth={1.5} />
          ))}
          {sorted.map((p) => (
            <circle
              key={p.id}
              cx={p.x}
              cy={p.y}
              r={r(p.value)}
              fillOpacity={0.9}
              strokeWidth={1}
              className={cx('stroke-jc-ink', p.id === highlightId ? 'fill-jc-chart-2' : 'fill-jc-chart-context')}
            />
          ))}
        </svg>
        {sorted
          .filter((p) => named.has(p.id))
          .map((p) => {
            const left = ((p.x - vx) / vw) * 100;
            const rightSide = p.labelSide ? p.labelSide === 'right' : left < 60;
            return (
              <span
                key={p.id}
                aria-hidden="true"
                className={cx(
                  'absolute -translate-y-1/2 text-xs font-semibold whitespace-nowrap text-jc-ink',
                  rightSide ? 'pl-2' : '-translate-x-full pr-2',
                  p.id !== highlightId && WIDE_ONLY[layout],
                )}
                style={{
                  left: `calc(${left}% + ${rightSide ? 1 : -1} * ${(r(p.value) / vw) * 100}%)`,
                  top: `${((p.y - vy) / vh) * 100}%`,
                }}
              >
                {p.label} <span className="font-display font-bold tabular-nums">{p.display}</span>
              </span>
            );
          })}
      </div>
      {keyed.length ? (
        <ul
          aria-hidden="true"
          className={cx('mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-jc-ink-secondary', NARROW_ONLY[layout])}
        >
          {keyed.map((p) => (
            <li key={p.id}>
              {p.label} <span className="font-display font-bold text-jc-ink tabular-nums">{p.display}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** The two-circle size key for a BubbleMap: circle area follows the value. */
export function BubbleKey({
  label,
  sizes,
  ratio,
}: {
  /** "Circle area = open jobs". */
  label: ReactNode;
  /** The two sizes shown, formatted and translated ("4,000 and 800"). */
  sizes: ReactNode;
  /** The small circle's value as a share of the large one's. */
  ratio: number;
}) {
  const rs = Math.sqrt(Math.max(0, Math.min(1, ratio)));
  return (
    <span className="inline-flex items-end gap-2">
      <span className="relative size-12" aria-hidden="true">
        <span className="absolute inset-0 rounded-full ring-1 ring-jc-ink-secondary" />
        <span
          className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full ring-1 ring-jc-ink-secondary"
          style={{ width: `${rs * 100}%`, height: `${rs * 100}%` }}
        />
      </span>
      <span className="flex flex-col">
        {label}
        <span>{sizes}</span>
      </span>
    </span>
  );
}
